import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { PaginasService } from '../../../../service/paginas.service';
import { TestsService } from '../../../../service/tests.service';
import { IntentoTest, Pagina, RespuestaDto } from '../../../../service/types/cursos.types';

interface TestRunnerProps {
  paginaId: string;
  onAprobado?: (intento: IntentoTest) => void;
}

export default function TestRunner({ paginaId, onAprobado }: TestRunnerProps) {
  const [pagina, setPagina] = useState<Pagina | null>(null);
  const [respuestas, setRespuestas] = useState<Record<string, RespuestaDto>>({});
  const [resultado, setResultado] = useState<IntentoTest | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    PaginasService.findOneForRender(paginaId).then(setPagina);
    setRespuestas({});
    setResultado(null);
  }, [paginaId]);

  const setRespuestaOpcion = (preguntaId: string, opcionId: string) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: { preguntaId, opcionId } }));
  };

  const setRespuestaTexto = (preguntaId: string, respuestaTexto: string) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: { preguntaId, respuestaTexto } }));
  };

  const handleSubmit = async () => {
    if (!pagina) return;
    setSubmitting(true);
    try {
      const intento = await TestsService.submitIntento(paginaId, {
        respuestas: Object.values(respuestas),
      });
      setResultado(intento);
      if (intento.aprobado) onAprobado?.(intento);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReintentar = () => {
    setRespuestas({});
    setResultado(null);
  };

  if (!pagina) return <p>Cargando test...</p>;

  return (
    <Card>
      <Card.Body>
        {(pagina.preguntas ?? []).map((pregunta) => (
          <Form.Group key={pregunta.id} className="mb-4">
            <Form.Label>
              <strong>{pregunta.enunciado}</strong>
            </Form.Label>
            {pregunta.tipo === 'opcion_multiple' ? (
              (pregunta.opciones ?? []).map((opcion) => (
                <Form.Check
                  key={opcion.id}
                  type="radio"
                  name={`pregunta-${pregunta.id}`}
                  id={`opcion-${opcion.id}`}
                  label={opcion.texto}
                  disabled={!!resultado}
                  checked={respuestas[pregunta.id]?.opcionId === opcion.id}
                  onChange={() => setRespuestaOpcion(pregunta.id, opcion.id)}
                />
              ))
            ) : (pregunta.opciones ?? []).length > 0 ? (
              (pregunta.opciones ?? []).map((opcion) => (
                <Form.Check
                  key={opcion.id}
                  type="radio"
                  name={`pregunta-${pregunta.id}`}
                  id={`opcion-${opcion.id}`}
                  label={opcion.texto}
                  disabled={!!resultado}
                  checked={respuestas[pregunta.id]?.opcionId === opcion.id}
                  onChange={() => setRespuestaOpcion(pregunta.id, opcion.id)}
                />
              ))
            ) : (
              <Form.Control
                as="textarea"
                rows={2}
                disabled={!!resultado}
                value={respuestas[pregunta.id]?.respuestaTexto ?? ''}
                onChange={(e) => setRespuestaTexto(pregunta.id, e.target.value)}
              />
            )}
          </Form.Group>
        ))}

        {resultado ? (
          <>
            <Alert variant={resultado.aprobado ? 'success' : 'danger'}>
              {resultado.aprobado
                ? `¡Aprobado! Puntaje: ${resultado.puntaje}%`
                : `No aprobado. Puntaje: ${resultado.puntaje}%. Puede reintentar.`}
            </Alert>
            {!resultado.aprobado && (
              <Button variant="primary" onClick={handleReintentar}>
                Reintentar
              </Button>
            )}
          </>
        ) : (
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Enviando...' : 'Enviar respuestas'}
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}
